import React, { useCallback, useMemo } from 'react';
import { I18n } from 'react-redux-i18n';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import moment from 'moment';
import CurrencyHelper from '../../../helpers/currencyHelper';
import styles from './RevenueChart.module.css';

// Configuration constants
const CHART_AGGREGATION_THRESHOLD = 180; // Days threshold for weekly aggregation

// Date format configuration
const DATE_FORMATS = {
  currentYear: 'DD MMM',       // Format when date is in current year (e.g., "15 Dec")
  otherYear: 'DD MMM YYYY'     // Format when date is in a different year (e.g., "15 Dec 2024")
};

// Chart color configuration
const CHART_COLORS = {
  currentPeriod: {
    stroke: '#ff6b35',      // Line color on chart
    tooltip: '#ffffff'      // Text color in tooltip
  },
  previousPeriod: {
    stroke: '#ffb366',      // Line color on chart
    tooltip: '#ffffff'      // Text color in tooltip
  }
};

interface RevenueChartProps {
  data: {
    current: any[];
    previous: any[];
    period: number;
  };
  isLoading?: boolean;
  timePeriod: 'last7Days' | 'last30Days' | 'last90Days' | 'ytd' | 'customRange';
}

interface ChartDataPoint {
  date: string;
  dateObj: Date;
  timestamp: number;
  currentRevenue: number;
  previousRevenue: number;
  currency: string;
}

// Custom tooltip component
const CustomTooltip = (props: any) => {
  const { active, payload } = props;
  
  if (!active || !payload || !payload.length) {
    return null;
  }

  const data = payload[0]?.payload as ChartDataPoint;
  const currentYear = new Date().getFullYear();
  const dateYear = moment(data?.dateObj).year();
  const dateFormat = dateYear === currentYear ? DATE_FORMATS.currentYear : DATE_FORMATS.otherYear;
  const dateLabel = moment(data?.dateObj).format(dateFormat);
  
  // Check if dark mode is active
  const isDarkMode = document.body.classList.contains('dark-theme');

  return (
    <div
      style={{
        background: isDarkMode ? '#333333' : '#ffffff',
        borderRadius: 8,
        padding: '8px 12px',
        color: isDarkMode ? '#ffffff' : '#000000',
        fontSize: 12,
        border: isDarkMode ? 'none' : '1px solid #e0e0e0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
      }}
    >
      <div style={{ marginBottom: 4, fontWeight: 'bold' }}>{dateLabel}</div>
        {payload.map((entry: any, index: number) => (
          <div key={index}>
            {entry.name}: {CurrencyHelper.formatCurrency(
            CurrencyHelper.roundToDecimal(entry.value as number, 0),
            data?.currency || 'aud'
          )}
        </div>
      ))}
    </div>
  );
};

export const RevenueChart: React.FC<RevenueChartProps> = ({
  data,
  isLoading = false,
  timePeriod
}) => {
  // Helper function to aggregate daily data into weekly data
  const aggregateDataToWeeks = useCallback((dailyData: any[]): any[] => {
    if (dailyData.length <= CHART_AGGREGATION_THRESHOLD) {
      return dailyData;
    }

    const weeklyData: { [key: string]: { currentRevenue: number; previousRevenue: number; currency: string; dates: Date[] } } = {};

    dailyData.forEach(item => {
      const weekStart = moment(item.dateObj).startOf('isoWeek');
      const weekKey = weekStart.format('YYYY-MM-DD');

      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = {
          currentRevenue: 0,
          previousRevenue: 0,
          currency: item.currency,
          dates: []
        };
      }

      weeklyData[weekKey].currentRevenue += item.currentRevenue || 0;
      weeklyData[weekKey].previousRevenue += item.previousRevenue || 0;
      weeklyData[weekKey].dates.push(item.dateObj);
    });

    return Object.entries(weeklyData)
      .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
      .map(([weekStart, weekData]) => ({
        date: moment(weekStart).format('D MMM'),
        dateObj: new Date(weekStart),
        timestamp: new Date(weekStart).getTime(),
        currentRevenue: weekData.currentRevenue,
        previousRevenue: weekData.previousRevenue,
        currency: weekData.currency
      }));
  }, []);

  // Transform and combine data for chart
  const chartData = useMemo((): ChartDataPoint[] => {
    if (!data.current.length) return [];

    // Sort current data by date
    const sortedCurrent = [...data.current].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    const sortedPrevious = [...data.previous].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // Create a map of previous data by month-day key for robust matching
    // This handles leap year (Feb 29) and any other date misalignments
    const previousByMonthDay: { [key: string]: number } = {};
    sortedPrevious.forEach(item => {
      const d = new Date(item.date);
      const monthDay = `${d.getMonth()}-${d.getDate()}`; // e.g., "1-29" for Feb 29
      
      // If Feb 29 (leap year), aggregate into Feb 28
      if (d.getMonth() === 1 && d.getDate() === 29) {
        const feb28Key = '1-28';
        previousByMonthDay[feb28Key] = (previousByMonthDay[feb28Key] || 0) + (item.raisedAmount || 0);
      } else {
        previousByMonthDay[monthDay] = (previousByMonthDay[monthDay] || 0) + (item.raisedAmount || 0);
      }
    });

    // Combine current and previous into unified dataset using month-day matching
    const combined = sortedCurrent.map((currentItem) => {
      const currentDate = new Date(currentItem.date);
      const monthDay = `${currentDate.getMonth()}-${currentDate.getDate()}`;
      
      return {
        date: moment(currentItem.date).format('MMM DD'),
        dateObj: currentDate,
        timestamp: currentDate.getTime(),
        currentRevenue: currentItem.raisedAmount || 0,
        previousRevenue: previousByMonthDay[monthDay] || 0,
        currency: currentItem.currency || 'aud'
      };
    });

    // Apply weekly aggregation if dataset is large
    return aggregateDataToWeeks(combined);
  }, [data, aggregateDataToWeeks]);

  // Date axis tick formatter
  const formatXAxisTick = useCallback((timestamp: number) => {
    const date = new Date(timestamp);
    if (timePeriod === 'last7Days' || timePeriod === 'last30Days') {
      return moment(date).format('MMM DD');
    }
    return moment(date).format('MMM DD');
  }, [timePeriod]);

  // Y-axis tick formatter
  const formatYAxisTick = useCallback((value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value.toString();
  }, []);

  if (isLoading && !data.current.length) {
    return (
      <div className={styles['revenueChartContainer']}>
        <div className={styles['revenueChartLoading']}>
          <div className={styles['customLoader']} />
          <p>{I18n.t('home.chart.loading')}</p>
        </div>
      </div>
    );
  }

  if (!data.current.length) {
    return (
      <div className={styles['revenueChartContainer']}>
        <div className={styles['revenueChartNoData']}>
          <p>{I18n.t('home.chart.noData')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['revenueChartContainer']}>
      {/* Chart */}
      <div className={styles['revenueChart']}>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={chartData}
            margin={{ top: 20, right: 20, left: 20, bottom: 5 }}
          >
            {/* Gradient definitions for area fill */}
            <defs>
              <linearGradient id="currentGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={CHART_COLORS.currentPeriod.stroke} stopOpacity={0.4} />
                <stop offset="100%" stopColor={CHART_COLORS.currentPeriod.stroke} stopOpacity={0.1} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeOpacity={0.1} vertical={false} />

            <XAxis
              dataKey="timestamp"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              tickFormatter={formatXAxisTick}
              tick={{ fontSize: 11, fill: '#666' }}
              axisLine={{ stroke: '#e0e0e0' }}
              tickLine={false}
              minTickGap={50}
            />

            <YAxis
              tickFormatter={formatYAxisTick}
              tick={{ fontSize: 11, fill: '#666' }}
              axisLine={false}
              tickLine={false}
              width={50}
              label={{
                value: I18n.t('home.chart.raisedAxisLabel'),
                angle: -90,
                position: 'insideLeft',
                offset: 0,
                fill: '#666',
                fontSize: 12
              }}
            />

            <Tooltip
              content={<CustomTooltip />}
              cursor={{ stroke: '#ccc', strokeWidth: 1 }}
            />

            <Legend 
              verticalAlign="top" 
              align="right" 
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ paddingBottom: 20 }}
              formatter={(value) => <span style={{ color: '#666' }}>{value}</span>}
            />

            {/* Current Period Line */}
            <Line
              type="monotone"
              dataKey="currentRevenue"
              name={I18n.t('home.chart.tooltipCurrentPeriod', { defaultValue: 'Current period' })}
              stroke={CHART_COLORS.currentPeriod.stroke}
              fill={CHART_COLORS.currentPeriod.tooltip}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 5, fill: CHART_COLORS.currentPeriod.stroke, stroke: '#ffffff', strokeWidth: 1 }}
              isAnimationActive={false}
            />

            {/* Previous Period Line */}
            <Line
              type="monotone"
              dataKey="previousRevenue"
              name={I18n.t('home.chart.tooltipPreviousPeriod', { defaultValue: 'Same period last year' })}
              stroke={CHART_COLORS.previousPeriod.stroke}
              fill={CHART_COLORS.previousPeriod.tooltip}
              strokeWidth={2}
              strokeDasharray="3 3"
              dot={false}
              activeDot={{ r: 5, fill: CHART_COLORS.previousPeriod.stroke, stroke: '#ffffff', strokeWidth: 1 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className={styles['loadingOverlay']}>
          <div className={styles['loadingContent']}>
            <div className={styles['customLoader']} />
            <p>{I18n.t('home.chart.loading')}</p>
          </div>
        </div>
      )}


    </div>
  );
};

export default RevenueChart;
